/**
 * ExclusiveContentSubscriptionRemapper
 *
 * Maps between the data and domain layers for exclusive-content
 * subscriptions:
 *   - ExclusiveContentSubscriptionStorage (SecureStorage) → Entity
 *   - ExclusiveContentSubscriptionDto    (Firestore wire) → Entity
 */
import type { ExclusiveContentSubscriptionDto } from '@/data/dtos/exclusiveContentSubscription/exclusiveContentSubscriptionDto';
import type { JsonObject } from '@/data/types/json';
import type { ExclusiveContentSubscriptionStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';

export class ExclusiveContentSubscriptionRemapper {
  static toStorage(dto: ExclusiveContentSubscriptionDto): ExclusiveContentSubscriptionStorage {
    return {
      userId: dto.userId,
      subscriberUserId: dto.subscriberUserId,
      createdDate: dto.createdDate,
      updatedDate: dto.updatedDate,
      expireDate: dto.expireDate,
      name: dto.name,
      username: dto.username,
      firstLastNameLower: dto.firstLastNameLower,
      lastFirstNameLower: dto.lastFirstNameLower,
      coins: dto.coins,
      meSubscriber: dto.meSubscriber,
      status: dto.status,
    };
  }

  static fromStorage(storage: ExclusiveContentSubscriptionStorage): ExclusiveContentSubscriptionEntity {
    return {
      userId: storage.userId,
      subscriberUserId: storage.subscriberUserId,
      createdDate: storage.createdDate,
      updatedDate: storage.updatedDate,
      expireDate: storage.expireDate,
      name: storage.name,
      username: storage.username,
      firstLastNameLower: storage.firstLastNameLower,
      lastFirstNameLower: storage.lastFirstNameLower,
      coins: storage.coins,
      meSubscriber: storage.meSubscriber,
      status: storage.status,
    };
  }

  static fromStorageList(storageList: ExclusiveContentSubscriptionStorage[]): ExclusiveContentSubscriptionEntity[] {
    return storageList.map(ExclusiveContentSubscriptionRemapper.fromStorage);
  }

  static fromDto(dto: ExclusiveContentSubscriptionDto): ExclusiveContentSubscriptionEntity {
    return {
      userId: dto.userId,
      subscriberUserId: dto.subscriberUserId,
      createdDate: dto.createdDate,
      updatedDate: dto.updatedDate,
      expireDate: dto.expireDate,
      name: dto.name,
      username: dto.username,
      firstLastNameLower: dto.firstLastNameLower,
      lastFirstNameLower: dto.lastFirstNameLower,
      coins: dto.coins,
      meSubscriber: dto.meSubscriber,
      status: dto.status,
    };
  }

  static fromDtoList(dtoList: ExclusiveContentSubscriptionDto[]): ExclusiveContentSubscriptionEntity[] {
    return dtoList.map(ExclusiveContentSubscriptionRemapper.fromDto);
  }

  /**
   * Defensive parse from raw Firestore `JsonObject` to a typed DTO.
   * Applies defaults so a malformed document never produces a partially-
   * populated entity downstream. Field names match the Firestore document
   * literally (Flutter uses `@JsonKey` aliases internally —
   * lib/data/models/exclusive_content_subscription.dart:31-37).
   */
  static fromJson(data: JsonObject): ExclusiveContentSubscriptionDto {
    return {
      userId: (data['userId'] as string | undefined) || '',
      subscriberUserId: (data['subscriberUserId'] as string | undefined) || '',
      createdDate: (data['createdDate'] as number | undefined) || 0,
      updatedDate: data['updatedDate'] as number | undefined,
      expireDate: data['expireDate'] as number | undefined,
      name: (data['name'] as string | undefined) || '',
      username: (data['username'] as string | undefined) || '',
      firstLastNameLower: (data['firstLastNameLower'] as string | undefined) || '',
      lastFirstNameLower: (data['lastFirstNameLower'] as string | undefined) || '',
      coins: data['coins'] as number | undefined,
      meSubscriber: (data['meSubscriber'] as boolean | undefined) || false,
      status: (data['status'] as number | undefined) || 0,
    };
  }

  static fromJsonList(dataList: JsonObject[]): ExclusiveContentSubscriptionDto[] {
    return dataList.map(ExclusiveContentSubscriptionRemapper.fromJson);
  }
}
